/**
 * Site and TMS Django group policy helpers for aircraft boards and the DSR.
 *
 * Group names that match a Site name restrict users to that site.
 * Group names that match a TMS name restrict users to that Type/Model/Series.
 */

const catalogName = (entry) => {
    if (entry == null) return '';
    if (typeof entry === 'string') return entry.trim();
    return String(entry.name || '').trim();
};

const catalogNames = (catalog) =>
    (Array.isArray(catalog) ? catalog : [])
        .map(catalogName)
        .filter(Boolean);

const namesMatch = (left, right) =>
    String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();

export function getMatchingGroups(groups, catalog) {
    const names = catalogNames(catalog);
    return (Array.isArray(groups) ? groups : []).filter((group) =>
        names.some((name) => namesMatch(group, name))
    );
}

export function getSiteGroups(groups, sites) {
    return getMatchingGroups(groups, sites);
}

export function getTmsGroups(groups, tmsList) {
    return getMatchingGroups(groups, tmsList);
}

export function getAircraftTmsName(aircraft) {
    if (!aircraft) return '';
    return String(
        aircraft.tms ||
        aircraft.tms_name ||
        aircraft.aircraft?.tms_name ||
        ''
    ).trim();
}

export function aircraftMatchesGroupPolicy(aircraft, { isStaff, groups, sites, tmsList } = {}) {
    if (isStaff) return true;
    const siteGroups = getSiteGroups(groups, sites);
    const tmsGroups = getTmsGroups(groups, tmsList);
    // No matching site/TMS groups: do not extra-restrict (backend already scoped;
    // catalogs may also still be loading).
    if (siteGroups.length === 0 && tmsGroups.length === 0) return true;
    if (siteGroups.length > 0) {
        const site = aircraft?.site || aircraft?.site_name || '';
        if (!siteGroups.some((group) => namesMatch(group, site))) return false;
    }
    if (tmsGroups.length === 0) return true;
    const tms = getAircraftTmsName(aircraft);
    return tmsGroups.some((group) => namesMatch(group, tms));
}

export function getGroupPolicyLabel(groups, sites, tmsList) {
    const siteGroups = getSiteGroups(groups, sites);
    const tmsGroups = getTmsGroups(groups, tmsList);
    return [...siteGroups, ...tmsGroups].join(', ');
}
